import React, { useEffect, useState } from 'react';

interface Props {
    /** URL a compartir. Si no viene, la actual. */
    url?: string;
    /** Texto que acompaña al link en WhatsApp/mail. */
    titulo: string;
    /** `inline` = fila compacta para las cards. `panel` = bloque con título, para la ficha. */
    variante?: 'inline' | 'panel';
}

/**
 * Botones de compartir. El link se arma con la URL absoluta porque lo que se pega en un
 * chat tiene que funcionar fuera del sitio.
 *
 * Se apoya en `navigator.share` cuando existe (celulares): abre el menú nativo del
 * sistema, que es lo que la gente espera en mobile. En desktop cae a los botones sueltos.
 */
const ShareButtons: React.FC<Props> = ({ url, titulo, variante = 'inline' }) => {
    const [href, setHref] = useState(url ?? '');
    const [copiado, setCopiado] = useState(false);
    const [puedeCompartirNativo, setPuedeCompartirNativo] = useState(false);

    // window no existe hasta que monta; la URL se resuelve acá y no en el render inicial.
    useEffect(() => {
        setHref(url ?? window.location.href);
        setPuedeCompartirNativo(typeof navigator !== 'undefined' && !!navigator.share);
    }, [url]);

    // El "¡Copiado!" vuelve solo a su estado normal. El timer se limpia si el componente
    // se desmonta antes (navegar a otra ficha mientras el cartel está visible).
    useEffect(() => {
        if (!copiado) return;
        const t = setTimeout(() => setCopiado(false), 2000);
        return () => clearTimeout(t);
    }, [copiado]);

    const copiar = async () => {
        try {
            // clipboard.writeText necesita contexto seguro (https o localhost). En http
            // plano tira y caemos al textarea + execCommand, que sigue andando en todos lados.
            await navigator.clipboard.writeText(href);
            setCopiado(true);
        } catch {
            const ta = document.createElement('textarea');
            ta.value = href;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand('copy');
                setCopiado(true);
            } catch {
                window.prompt('Copiá el link:', href);
            }
            document.body.removeChild(ta);
        }
    };

    const compartirNativo = async () => {
        try {
            await navigator.share({ title: titulo, url: href });
        } catch {
            // El usuario canceló el menú del sistema. No es un error.
        }
    };

    const e = encodeURIComponent;
    const enlaces = [
        {
            id: 'whatsapp',
            label: 'WhatsApp',
            href: `https://wa.me/?text=${e(`${titulo} — ${href}`)}`,
            icono: (
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.988 2.898 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            ),
        },
        {
            id: 'linkedin',
            label: 'LinkedIn',
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${e(href)}`,
            icono: (
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            ),
        },
        {
            id: 'x',
            label: 'X',
            href: `https://twitter.com/intent/tweet?text=${e(titulo)}&url=${e(href)}`,
            icono: (
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            ),
        },
        {
            id: 'mail',
            label: 'Email',
            href: `mailto:?subject=${e(titulo)}&body=${e(`Te comparto este trabajo de Iron Five:\n\n${titulo}\n${href}`)}`,
            icono: (
                <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67zM22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
            ),
        },
    ];

    const esPanel = variante === 'panel';

    // En las cards del listado el pie es angosto y comparte renglón con "Ver el caso":
    // con los cinco botones el bloque se parte en dos líneas y descuadra la grilla. Ahí
    // van solo los dos canales por los que realmente se pasa un trabajo, y el botón de
    // copiar queda como ícono. El juego completo vive en la ficha.
    const visibles = esPanel
        ? enlaces
        : enlaces.filter((l) => l.id === 'whatsapp' || l.id === 'linkedin');

    return (
        <div className={esPanel ? 'border-t border-gray-200 pt-6 mt-8' : ''}>
            {esPanel && (
                <p className="text-sm font-montserrat font-semibold text-grafito mb-3">
                    Compartir este trabajo
                </p>
            )}

            <div className={`flex items-center gap-2 ${esPanel ? 'flex-wrap' : ''}`}>
                {puedeCompartirNativo && (
                    <button
                        type="button"
                        onClick={compartirNativo}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-cyan text-white text-sm font-montserrat font-semibold rounded-lg hover:bg-opacity-90 transition-colors sm:hidden"
                    >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
                        </svg>
                        Compartir
                    </button>
                )}

                <button
                    type="button"
                    onClick={copiar}
                    aria-live="polite"
                    aria-label={copiado ? 'Link copiado' : 'Copiar link'}
                    title="Copiar link"
                    className={`inline-flex items-center justify-center gap-2 text-sm font-montserrat font-semibold rounded-lg border transition-colors ${
                        esPanel ? 'px-4 py-2' : 'w-10 h-10'
                    } ${
                        copiado
                            ? 'bg-lima border-lima text-grafito'
                            : 'bg-white border-gray-300 text-grafito hover:border-cyan hover:text-cyan'
                    }`}
                >
                    {copiado ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                    )}
                    {esPanel && (copiado ? '¡Copiado!' : 'Copiar link')}
                </button>

                {visibles.map((l) => (
                    <a
                        key={l.id}
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Compartir por ${l.label}`}
                        title={`Compartir por ${l.label}`}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 text-gray-600 hover:border-cyan hover:text-cyan transition-colors"
                    >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            {l.icono}
                        </svg>
                    </a>
                ))}
            </div>
        </div>
    );
};

export default ShareButtons;
